<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">取消</button>
        <button type="button" class="btn btn-outline-primary" @click="handlePreview" :disabled="submitting">
          {{ submitting ? '處理中...' : '預覽' }}
        </button>
        <button
          v-if="previewData"
          type="button"
          class="btn btn-primary"
          :disabled="submitting"
          @click="handleSubmit"
        >
          {{ submitting ? '建立中...' : '建立批次操作' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else class="form-wrapper">
      <form @submit.prevent="handleSubmit">
        <!-- 基本資訊 -->
        <div class="form-section">
          <h2 class="section-title">基本資訊</h2>
          
          <div class="form-group">
            <label class="form-label">操作類型 <span class="required">*</span></label>
            <select v-model="form.operationType" class="form-control" required @change="handleOperationTypeChange">
              <option value="">請選擇操作類型</option>
              <option value="update_members">更新會員</option>
              <option value="adjust_points">調整點數</option>
              <option value="issue_coupons">發放優惠券</option>
            </select>
            <span v-if="errors.operationType" class="error-text">{{ errors.operationType }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">操作名稱 <span class="required">*</span></label>
            <input
              v-model="form.operationName"
              type="text"
              class="form-control"
              placeholder="例如：補發點數、發放生日優惠券"
              required
            />
            <span v-if="errors.operationName" class="error-text">{{ errors.operationName }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">操作原因 <span class="required">*</span></label>
            <textarea
              v-model="form.operationReason"
              class="form-control"
              rows="3"
              placeholder="請說明執行此批次操作的原因"
              required
            ></textarea>
            <span v-if="errors.operationReason" class="error-text">{{ errors.operationReason }}</span>
          </div>
        </div>

        <!-- 目標會員選擇 -->
        <div class="form-section">
          <h2 class="section-title">目標會員選擇</h2>
          
          <div class="form-group">
            <label class="form-label">選擇方式 <span class="required">*</span></label>
            <select v-model="form.targetType" class="form-control" required @change="handleTargetTypeChange">
              <option value="">請選擇選擇方式</option>
              <option value="filter">篩選條件</option>
              <option value="member_list">會員列表</option>
            </select>
            <span v-if="errors.targetType" class="error-text">{{ errors.targetType }}</span>
          </div>

          <!-- 篩選條件 -->
          <div v-if="form.targetType === 'filter'" class="filter-conditions">
            <div class="form-group">
              <label class="form-label">會員等級</label>
              <select v-model="form.filterConditions.tierId" class="form-control" :disabled="tiersLoading">
                <option value="">全部等級</option>
                <option v-for="tier in tiers" :key="tier.id" :value="tier.tierCode">
                  {{ tier.tierName }}
                </option>
              </select>
              <div v-if="tiersLoading" class="form-text">載入中...</div>
            </div>

            <div class="form-group">
              <label class="form-label">最低消費金額</label>
              <input
                v-model.number="form.filterConditions.minSpentAmount"
                type="number"
                class="form-control"
                placeholder="0"
                min="0"
              />
            </div>

            <div class="form-group">
              <label class="form-label">最低消費次數</label>
              <input
                v-model.number="form.filterConditions.minPurchaseCount"
                type="number"
                class="form-control"
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          <!-- 會員列表 -->
          <div v-if="form.targetType === 'member_list'" class="member-list">
            <div class="form-group">
              <label class="form-label">會員識別資訊列表</label>
              <textarea
                v-model="memberIdsText"
                class="form-control"
                rows="5"
                placeholder="每行一個或使用半形逗號（,）分隔：會員 ID（UUID）、電話號碼（09xxxxxxxx）或 LINE User ID，最多 1000 筆&#10;例如：0901234567,0912345678 或每行一個"
                @input="handleMemberIdsInput"
              ></textarea>
              <small class="form-text">支援換行或半形逗號（,）分隔，每行一個會員 ID（UUID）、電話號碼（09xxxxxxxx）或 LINE User ID，最多 1000 筆。輸入全型逗號（，）會自動轉換為半形。</small>
            </div>
          </div>
        </div>

        <!-- 操作內容 -->
        <div class="form-section">
          <h2 class="section-title">操作內容</h2>
          
          <!-- 調整點數 -->
          <div v-if="form.operationType === 'adjust_points'">
            <div class="form-group">
              <label class="form-label">調整類型 <span class="required">*</span></label>
              <select v-model="form.operationData.adjustmentType" class="form-control" required>
                <option value="">請選擇調整類型</option>
                <option value="add">增加點數</option>
                <option value="deduct">扣除點數</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">點數 <span class="required">*</span></label>
              <input
                v-model.number="form.operationData.points"
                type="number"
                class="form-control"
                placeholder="0"
                min="1"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">到期日期（選填）</label>
              <input
                v-model="form.operationData.expiryDate"
                type="date"
                class="form-control"
              />
            </div>
          </div>

          <!-- 發放優惠券 -->
          <div v-if="form.operationType === 'issue_coupons'">
            <div class="form-group">
              <label class="form-label">優惠券範本 <span class="required">*</span></label>
              <select v-model="form.operationData.templateId" class="form-control coupon-template-select" required :disabled="couponTemplatesLoading">
                <option value="">請選擇優惠券範本</option>
                <option v-for="template in couponTemplates" :key="template.id" :value="template.id">
                  {{ template.templateName }} - {{ formatCouponTypeText(template.couponType) }} {{ formatDiscountValue(template) }}
                  <span v-if="template.validityDays"> (有效 {{ template.validityDays }} 天)</span>
                  <span v-else-if="template.fixedStartDate && template.fixedEndDate"> ({{ template.fixedStartDate }} ~ {{ template.fixedEndDate }})</span>
                </option>
              </select>
              <div v-if="couponTemplatesLoading" class="form-text">載入中...</div>
              <div v-if="selectedTemplate" class="template-info">
                <div class="info-badge">
                  <span class="badge badge-primary">{{ formatCouponTypeText(selectedTemplate.couponType) }}</span>
                  <span class="badge badge-info">折扣：{{ formatDiscountValue(selectedTemplate) }}</span>
                  <span v-if="selectedTemplate.minPurchaseAmount > 0" class="badge badge-warning">
                    最低消費：${{ selectedTemplate.minPurchaseAmount }}
                  </span>
                </div>
              </div>
              <span v-if="errors.templateId" class="error-text">{{ errors.templateId }}</span>
            </div>

            <div class="form-group">
              <label class="form-label">發放數量 <span class="required">*</span></label>
              <input
                v-model.number="form.operationData.quantity"
                type="number"
                class="form-control"
                placeholder="1"
                min="1"
                required
              />
              <small class="form-text">每位會員發放的優惠券張數</small>
            </div>
            
            <div class="form-group">
              <label class="form-label">發放原因</label>
              <textarea
                v-model="form.operationData.reason"
                class="form-control"
                rows="2"
                placeholder="例如：年終回饋活動、會員關懷禮"
              ></textarea>
              <small class="form-text">記錄發放原因，方便日後追蹤</small>
            </div>
          </div>

          <!-- 更新會員 -->
          <div v-if="form.operationType === 'update_members'">
            <div class="form-group">
              <label class="form-label">操作類型 <span class="required">*</span></label>
              <select v-model="form.operationData.operation" class="form-control" required>
                <option value="">請選擇操作類型</option>
                <option value="add_tags">新增標籤</option>
                <option value="remove_tags">移除標籤</option>
                <option value="update_tier">更新等級</option>
              </select>
              <span v-if="errors.operation" class="error-text">{{ errors.operation }}</span>
            </div>

            <!-- 新增/移除標籤 -->
            <div v-if="form.operationData.operation === 'add_tags' || form.operationData.operation === 'remove_tags'">
              <div class="form-group">
                <label class="form-label">標籤 ID 列表 <span class="required">*</span></label>
                <textarea
                  v-model="form.operationData.tagIdsText"
                  class="form-control"
                  rows="3"
                  placeholder="每行一個標籤 ID（UUID）"
                  @input="handleTagIdsInput"
                ></textarea>
                <small class="form-text">每行一個標籤 ID，最多 100 個</small>
                <span v-if="errors.tagIds" class="error-text">{{ errors.tagIds }}</span>
              </div>
            </div>

            <!-- 更新等級 -->
            <div v-if="form.operationData.operation === 'update_tier'">
              <div class="form-group">
                <label class="form-label">會員等級 <span class="required">*</span></label>
                <select v-model="form.operationData.tierId" class="form-control" required :disabled="tiersLoading">
                  <option value="">請選擇會員等級</option>
                  <option v-for="tier in tiers" :key="tier.id" :value="tier.tierCode">
                    {{ tier.tierName }}
                  </option>
                </select>
                <div v-if="tiersLoading" class="form-text">載入中...</div>
                <span v-if="errors.tierId" class="error-text">{{ errors.tierId }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 預覽資訊 -->
        <div v-if="previewData" class="form-section">
          <h2 class="section-title">預覽資訊</h2>
          <div class="preview-info">
            <p><strong>影響會員數：</strong>{{ previewData.previewCount }} 人</p>
            <div v-if="previewData.previewMembers && previewData.previewMembers.length > 0">
              <p><strong>預覽會員列表（前 10 筆）：</strong></p>
              <ul class="preview-members">
                <li v-for="member in previewData.previewMembers.slice(0, 10)" :key="member.memberId">
                  {{ member.memberName }} ({{ member.memberTier || '無等級' }})
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 錯誤訊息 -->
        <div v-if="submitError" class="alert alert-danger">
          {{ submitError }}
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { batchOperationService } from '@/api/modules/batchOperations.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'CreateBatchOperation',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('建立批次操作');
    const loading = ref(false);
    const error = ref(null);
    const submitting = ref(false);
    const submitError = ref('');
    const previewData = ref(null);
    const memberIdsText = ref('');
    const tiers = ref([]);
    const tiersLoading = ref(false);
    const couponTemplates = ref([]);
    const couponTemplatesLoading = ref(false);

    const form = ref({
      operationType: '',
      operationName: '',
      operationReason: '',
      targetType: '',
      filterConditions: {
        tierId: '',
        tagIds: [],
        minSpentAmount: null,
        minPurchaseCount: null
      },
      memberIds: [],
      operationData: {
        // 調整點數
        adjustmentType: '',
        points: null,
        expiryDate: '',
        // 發放優惠券
        templateId: '',
        quantity: 1,
        // 更新會員
        operation: '',
        tagIdsText: '',
        tagIds: [],
        tierId: ''
      },
      executeImmediately: true
    });

    const errors = ref({});

    // 計算選中的優惠券模板
    const selectedTemplate = computed(() => {
      if (!form.value.operationData.templateId) return null;
      return couponTemplates.value.find(t => t.id === form.value.operationData.templateId);
    });

    // 格式化優惠券類型
    const formatCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    // 格式化折扣值
    const formatDiscountValue = (template) => {
      if (!template) return '';
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      } else if (template.couponType === 'fixed_amount') {
        return `$${template.discountValue}`;
      } else if (template.couponType === 'free_shipping') {
        return '免運費';
      } else if (template.couponType === 'gift') {
        return '贈品';
      }
      return '';
    };

    const handleBack = () => {
      router.push('/client/batch-operations');
    };

    const handleOperationTypeChange = () => {
      // 重置操作資料
      form.value.operationData = {
        adjustmentType: '',
        points: null,
        expiryDate: '',
        templateId: '',
        quantity: 1,
        operation: '',
        tagIdsText: '',
        tagIds: [],
        tierId: ''
      };
      previewData.value = null;
    };

    const handleTagIdsInput = () => {
      const lines = form.value.operationData.tagIdsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      form.value.operationData.tagIds = lines.slice(0, 100); // 最多 100 個
    };

    const handleTargetTypeChange = () => {
      if (form.value.targetType === 'member_list') {
        // 選擇會員列表時，將 filterConditions 設為 null
        form.value.filterConditions = null;
      } else {
        // 選擇篩選條件時，重置 filterConditions 為空物件，並清空 memberIds
        form.value.filterConditions = {
          tierId: '',
          tagIds: [],
          minSpentAmount: null,
          minPurchaseCount: null
        };
        form.value.memberIds = [];
        memberIdsText.value = '';
      }
      previewData.value = null;
    };

    const handleMemberIdsInput = () => {
      // 全型逗號自動轉換為半形
      const normalized = memberIdsText.value.replace(/，/g, ',');
      if (normalized !== memberIdsText.value) {
        memberIdsText.value = normalized;
      }

      // 支援換行和逗號分隔
      let identifiers = [];
      
      // 先按換行分割
      const lines = memberIdsText.value.split('\n');
      
      for (const line of lines) {
        // 每行再按逗號分割
        const parts = line.split(',').map(part => part.trim()).filter(part => part.length > 0);
        identifiers.push(...parts);
      }
      
      // 去重並限制數量
      const uniqueIdentifiers = [...new Set(identifiers)];
      form.value.memberIds = uniqueIdentifiers.slice(0, 1000);
    };

    const validateForm = () => {
      errors.value = {};

      if (!form.value.operationType) {
        errors.value.operationType = '請選擇操作類型';
      }
      if (!form.value.operationName) {
        errors.value.operationName = '請輸入操作名稱';
      }
      if (!form.value.operationReason) {
        errors.value.operationReason = '請輸入操作原因';
      }
      if (!form.value.targetType) {
        errors.value.targetType = '請選擇目標會員選擇方式';
      }

      if (form.value.operationType === 'adjust_points') {
        if (!form.value.operationData.adjustmentType) {
          errors.value.adjustmentType = '請選擇調整類型';
        }
        if (!form.value.operationData.points || form.value.operationData.points <= 0) {
          errors.value.points = '請輸入有效的點數';
        }
      }

      if (form.value.operationType === 'issue_coupons') {
        if (!form.value.operationData.templateId) {
          errors.value.templateId = '請選擇優惠券範本';
        }
        if (!form.value.operationData.quantity || form.value.operationData.quantity <= 0) {
          errors.value.quantity = '請輸入有效的發放數量';
        }
      }

      if (form.value.operationType === 'update_members') {
        if (!form.value.operationData.operation) {
          errors.value.operation = '請選擇操作類型';
        } else {
          if ((form.value.operationData.operation === 'add_tags' || form.value.operationData.operation === 'remove_tags')) {
            if (!form.value.operationData.tagIds || form.value.operationData.tagIds.length === 0) {
              errors.value.tagIds = '請輸入至少一個標籤 ID';
            }
          } else if (form.value.operationData.operation === 'update_tier') {
            if (!form.value.operationData.tierId) {
              errors.value.tierId = '請選擇會員等級';
            }
          }
        }
      }

      return Object.keys(errors.value).length === 0;
    };

    const prepareFormData = () => {
      const formData = { ...form.value };
      
      // 根據目標類型清理不需要的欄位
      if (formData.targetType === 'member_list') {
        // 選擇會員列表時，不需要 filterConditions
        formData.filterConditions = null;
        // 確保 memberIds 是陣列，並過濾空字串
        if (!formData.memberIds || !Array.isArray(formData.memberIds)) {
          formData.memberIds = [];
        } else {
          formData.memberIds = formData.memberIds
            .map(id => id && typeof id === 'string' ? id.trim() : id)
            .filter(id => id && id.length > 0);
        }
      } else if (formData.targetType === 'filter') {
        // 選擇篩選條件時，不需要 memberIds
        formData.memberIds = [];
        // 清理空的 filterConditions 屬性
        if (formData.filterConditions) {
          const cleaned = {};
          if (formData.filterConditions.tierId && formData.filterConditions.tierId.trim()) {
            cleaned.tierId = formData.filterConditions.tierId.trim();
          }
          if (formData.filterConditions.tagIds && formData.filterConditions.tagIds.length > 0) {
            cleaned.tagIds = formData.filterConditions.tagIds;
          }
          if (formData.filterConditions.minSpentAmount !== null && formData.filterConditions.minSpentAmount !== undefined) {
            cleaned.minSpentAmount = formData.filterConditions.minSpentAmount;
          }
          if (formData.filterConditions.minPurchaseCount !== null && formData.filterConditions.minPurchaseCount !== undefined) {
            cleaned.minPurchaseCount = formData.filterConditions.minPurchaseCount;
          }
          // 如果沒有任何條件，設為 null
          formData.filterConditions = Object.keys(cleaned).length > 0 ? cleaned : null;
        }
      }
      
      // 如果是更新會員，需要轉換資料格式
      if (formData.operationType === 'update_members') {
        const updates = {};
        if (formData.operationData.operation === 'add_tags' || formData.operationData.operation === 'remove_tags') {
          updates.tagIds = formData.operationData.tagIds || [];
        } else if (formData.operationData.operation === 'update_tier') {
          updates.tierId = formData.operationData.tierId;
        }
        
        formData.operationData = {
          operation: formData.operationData.operation,
          updates
        };
      }
      
      return formData;
    };

    const handlePreview = async () => {
      if (!validateForm()) {
        submitError.value = '請填寫所有必填欄位';
        return;
      }

      submitting.value = true;
      submitError.value = '';

      try {
        const formData = prepareFormData();
        const response = await batchOperationService.createBatchOperation(formData);

        if (response && response.success && response.data) {
          previewData.value = response.data;
          await showSuccess('預覽成功', `將影響 ${response.data.previewCount} 位會員`);
        } else {
          submitError.value = response?.message || '預覽失敗';
        }
      } catch (err) {
        console.error('預覽失敗:', err);
        submitError.value = err.message || '預覽失敗';
        await showError('預覽失敗', submitError.value);
      } finally {
        submitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!previewData.value) {
        await showError('請先預覽', '請先點擊「預覽」按鈕確認影響範圍');
        return;
      }

      if (!validateForm()) {
        submitError.value = '請填寫所有必填欄位';
        return;
      }

      submitting.value = true;
      submitError.value = '';

      try {
        const formData = prepareFormData();
        const response = await batchOperationService.createBatchOperation(formData);

        if (response && response.success && response.data) {
          await showSuccess('建立成功', '批次操作已建立，請前往列表頁面執行');
          router.push(`/client/batch-operations/${response.data.batchId}`);
        } else {
          submitError.value = response?.message || '建立失敗';
        }
      } catch (err) {
        console.error('建立失敗:', err);
        submitError.value = err.message || '建立失敗';
        await showError('建立失敗', submitError.value);
      } finally {
        submitting.value = false;
      }
    };

    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          // memberTierService.getTiers 返回的是陣列，不是分頁格式
          tiers.value = Array.isArray(response.data) ? response.data : [];
        }
      } catch (err) {
        console.error('載入會員等級失敗:', err);
      } finally {
        tiersLoading.value = false;
      }
    };

    const loadCouponTemplates = async () => {
      couponTemplatesLoading.value = true;
      try {
        const response = await couponService.getTemplatesDropdown({
          isActive: true
        });
        if (response && response.success && response.data) {
          // dropdown API 返回的是陣列
          couponTemplates.value = Array.isArray(response.data) ? response.data : [];
        }
      } catch (err) {
        console.error('載入優惠券範本失敗:', err);
      } finally {
        couponTemplatesLoading.value = false;
      }
    };

    onMounted(() => {
      loadTiers();
      loadCouponTemplates();
      
      // 從 URL 參數預填表單
      if (route.query.operationType) {
        form.value.operationType = route.query.operationType;
      }
      if (route.query.templateId) {
        form.value.operationData.templateId = route.query.templateId;
      }
    });

    return {
      pageTitle,
      loading,
      error,
      submitting,
      submitError,
      form,
      errors,
      previewData,
      memberIdsText,
      selectedTemplate,
      handleBack,
      handleOperationTypeChange,
      handleTargetTypeChange,
      handleMemberIdsInput,
      handleTagIdsInput,
      handlePreview,
      handleSubmit,
      formatCouponTypeText,
      formatDiscountValue,
      tiers,
      tiersLoading,
      couponTemplates,
      couponTemplatesLoading
    };
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }
.btn-outline-primary { background: #fff; color: #3b82f6; border: 1px solid #3b82f6; }
.btn-outline-primary:hover:not(:disabled) { background: #eff6ff; }
.btn-outline-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.form-section {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}

.filter-conditions,
.member-list {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.preview-info {
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 4px;
}

.preview-members {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0 0;
}

.preview-members li {
  padding: 0.25rem 0;
  border-bottom: 1px solid #ddd;
}

.preview-members li:last-child {
  border-bottom: none;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-top: 16px;
}

.alert-danger {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.required {
  color: #dc3545;
}

.coupon-template-select {
  font-size: 14px;
}

.template-info {
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.info-badge {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.info-badge .badge {
  font-size: 12px;
  padding: 4px 8px;
}
</style>
